/**
 * Room factory.
 *
 * Creates a Room instance that manages room state (mates) and game state.
 * This is shared logic used by both the worker (server) and web app (local mode).
 *
 * The room is composed of five modules:
 * - mates: mate management (updateMates, flushMates)
 * - game: game lifecycle (start, end, makeMove)
 * - setup: pre-game setup (openGame, choosePlayerCount, assignMates, startGameFromSetup)
 * - state: state management (getRoomState)
 * - options: game options management (get, set, effective)
 * - hosting: hosts, managed mode, and lifecycle authority
 * - curation: the owner's selection of games a curated room offers
 */

import type { Room, RoomCaller, RoomContainer } from '../types/room.types'
import type { BroadcastListener } from '../types/broadcast.types'
import type { StartGameArgs, RestartGameArgs } from '../schemas/action.schemas'
import { makeMatesInRoom } from './matesInRoom/matesInRoom'
import { makeGameInRoom } from './gameInRoom/gameInRoom'
import { makeRoomState } from './roomState/roomState'
import { makeGameSetup } from './gameSetup/gameSetup'
import { makeGameOptions } from './gameOptions/gameOptions'
import { makeGameChannelPreference } from './gameChannelPreference/gameChannelPreference'
import { makeGamelessMode } from './gamelessMode/gamelessMode'
import { makeChallengeConfigurationPreference } from './challengeConfigurationPreference/challengeConfigurationPreference'
import { lifecycleAuthorityError, makeHosting, seatEligibleMates, unseatMates } from './hosting/hosting'
import { makeCuration } from './curation/curation'

/**
 * Creates a Room instance that manages room state (mates) and game state.
 */
export function makeRoom(container: RoomContainer): Room {
  // Create modules in dependency order:
  // - gameModule and optionsModule are independent
  // - setupModule depends on gameModule and optionsModule for starting games
  const matesModule = makeMatesInRoom(container)
  const gameModule = makeGameInRoom(container)
  const stateModule = makeRoomState(container)
  const optionsModule = makeGameOptions(container)
  const channelPreferenceModule = makeGameChannelPreference(container)
  const gamelessModeModule = makeGamelessMode(container)
  const challengeConfigurationPreferenceModule = makeChallengeConfigurationPreference(container)
  const hostingModule = makeHosting(container)
  const curationModule = makeCuration(container)

  const eligibleMates = async (gameId: string) =>
    seatEligibleMates(container.storage.getState(), optionsModule.getHostPlays(gameId))

  /** Mates in the room who may not take a seat in the game. */
  const ineligibleMateIds = async (gameId: string) => {
    const eligibleIds = new Set((await eligibleMates(gameId)).map((m) => m.mateId))
    return new Set(container.storage.getState().mates.map((m) => m.mateId).filter((id) => !eligibleIds.has(id)))
  }

  // Setup module can start games directly via gameModule
  const setupModule = makeGameSetup({
    ...container,
    gameStarter: gameModule,
    optionsProvider: optionsModule,
    challengeConfigurationPreferenceProvider: challengeConfigurationPreferenceModule,
    seatEligibility: { eligibleMates },
  })

  /** In managed mode only hosts start or end games; returns the refusal, if any. */
  const authorityError = (caller: RoomCaller | undefined) =>
    lifecycleAuthorityError(container.storage.getState(), caller)

  /** Wraps a lifecycle action so the room refuses it for non-hosts in managed mode. */
  function hostGated<A, R extends { success: boolean }>(action: (args: A) => Promise<R>) {
    return async (args: A, caller?: RoomCaller): Promise<R | { success: false; error: string }> => {
      const error = authorityError(caller)
      return error ? { success: false, error } : action(args)
    }
  }

  // Wrap startGame to auto-apply effective options when not explicitly provided
  const startGame: Room['startGame'] = async (args: StartGameArgs, caller?: RoomCaller) => {
    const error = authorityError(caller)
    if (error) return { success: false, error }
    const { gameId, ...rest } = args
    let options = { ...rest }

    // Default seating excludes hosts who observe under Host plays
    if (!options.mateIDs && !options.mateGroups) {
      options.mateIDs = (await eligibleMates(gameId)).map((m) => m.mateId)
    }

    // Auto-apply effective options if not explicitly provided
    if (!options.gameOptions) {
      const slotCount =
        options.mateGroups?.length ??
        options.mateIDs?.length ??
        container.storage.getState().mates.length

      const effectiveOptions = await optionsModule.getEffectiveOptions(gameId, slotCount)
      if (Object.keys(effectiveOptions).length > 0) {
        options = { ...options, gameOptions: effectiveOptions }
      }
    }

    return gameModule.startGame({ gameId, ...options })
  }

  // Restart the active game with updated options
  const restartGame: Room['restartGame'] = async (args: RestartGameArgs, caller?: RoomCaller) => {
    const error = authorityError(caller)
    if (error) return { success: false, error }
    const restartOptions = args.options
    const state = container.storage.getState()

    if (!state.activeGame) {
      return { success: false, error: 'No active game to restart' }
    }

    const { gameId, challengeConfiguration } = state.activeGame

    // If new options provided, save them first
    if (restartOptions?.scope && restartOptions?.values) {
      await optionsModule.setSavedGameOptions({
        gameId,
        scope: restartOptions.scope,
        values: restartOptions.values,
        playerCount: restartOptions.playerCount,
      })
    }

    // Same participants, except hosts the saved Host plays now leaves out
    const seats = unseatMates(
      { mateGroups: state.activeGame.mateGroups ?? [], observerIDs: state.activeGame.observerIDs ?? [] },
      await ineligibleMateIds(gameId),
    )
    if (!seats) {
      return { success: false, error: 'Hosts no longer take seats, which would leave a seat empty. Close the game and start it again to choose seats.' }
    }

    // End current game
    await gameModule.endGame()

    // Get effective options for restart
    const effectiveOptions = await optionsModule.getEffectiveOptions(gameId, seats.mateGroups.length)

    const gameResult = await gameModule.startGame({
      gameId,
      ...seats,
      challengeConfiguration,
      gameOptions: Object.keys(effectiveOptions).length > 0 ? effectiveOptions : undefined,
    })

    return {
      success: gameResult.success,
      error: gameResult.error,
    }
  }

  // Subscribe to messages - delegates to broadcastChannel
  const onMessage: Room['onMessage'] = (listener: BroadcastListener) => {
    return container.broadcastChannel.onMessage(listener)
  }

  return {
    // Mate operations
    updateMates: async (args, caller) => {
      const result = await matesModule.updateMates(args)
      if (result.success) hostingModule.reconcileAfterMatesUpdate(args.clientId, caller)
      return result
    },
    flushMates: matesModule.flushMates,
    getThumbnailHistory: matesModule.getThumbnailHistory,
    deleteThumbnailFromHistory: matesModule.deleteThumbnailFromHistory,

    // Game operations
    startGame,
    endGame: hostGated(() => gameModule.endGame()),
    makeMove: gameModule.makeMove,
    forceGameState: gameModule.forceGameState,
    updateGameVersion: gameModule.updateGameVersion,
    setActiveGameVersion: gameModule.setActiveGameVersion,

    // Setup operations (setupModule handles game starting internally)
    openGame: hostGated(setupModule.openGame),
    choosePlayerCount: hostGated(setupModule.choosePlayerCount),
    updateMateAssignment: setupModule.updateMateAssignment,
    startGameFromSetup: hostGated(() => setupModule.startGameFromSetup()),
    cancelGameSetup: hostGated(() => setupModule.cancelGameSetup()),
    getGameSetup: setupModule.getGameSetup,

    // State operations
    getRoomState: stateModule.getRoomState,

    // Options operations
    getSavedGameOptions: optionsModule.getSavedGameOptions,
    setSavedGameOptions: optionsModule.setSavedGameOptions,
    restartGame,

    // Game channel preference operations
    getGameChannelPreference: channelPreferenceModule.getGameChannelPreference,
    setGameChannelPreference: channelPreferenceModule.setGameChannelPreference,

    getChallengeConfigurationPreference: challengeConfigurationPreferenceModule.getChallengeConfigurationPreference,
    setChallengeConfigurationPreference: challengeConfigurationPreferenceModule.setChallengeConfigurationPreference,

    // Gameless mode operations
    setGamelessMode: gamelessModeModule.setGamelessMode,

    // Hosting operations
    setHost: hostingModule.setHost,
    setManagedMode: hostingModule.setManagedMode,
    setHostTerm: hostingModule.setHostTerm,

    // Curation operations
    setCuratedMode: curationModule.setCuratedMode,
    setCuratedGames: curationModule.setCuratedGames,

    // Subscription operations
    onMessage,
  }
}
